import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CollapseIcon, ExpandIcon } from '@/components/icons';
import { Trans, useTranslation } from 'react-i18next';
import {
  billingApi,
  type PaymentMethodResult,
  type Statement,
  type StatementLine,
  type StatementTaxLine,
} from '@/api/billing';
import type { TFunction } from 'i18next';
import { ChangeCardForm } from '@/features/billing/ChangeCardForm';
import { SellerLine } from '@/features/billing/SellerLine';
import { TaxLocationBanner } from '@/features/billing/TaxLocationBanner';
import { useClientConfigDisplay } from '@/features/billing/useStatementDescriptor';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { completeClientAction } from '@/features/billing/completeClientAction';
import { awaitSubscriberActivation } from '@/features/subscribe/subscribeAttempt';
import { bpToPercentText, formatList, formatPercentText } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { AiWalletSection } from '@/features/aiWallet/AiWalletSection';
import { formatWhen } from './formatWhen';
import { useBillingProfile } from './billingProfileContext';
import type { SettingsNotify } from './settingsHref';

interface BillingTabProps {
  onNotify: SettingsNotify;
}

export function BillingTab({ onNotify }: BillingTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const zone = me.user.timezone ?? 'UTC';
  // The page's one billing read (TMMAPP-0112): shared with the Subscription section.
  const { status, profile, error: loadError, reload: load } = useBillingProfile();
  const [changeOpen, setChangeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingBusy, setPendingBusy] = useState(false);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  // The card number lives in Stripe's iframe: the SPA sees only that the details are complete.
  // Complete-but-unsubmitted card details are the unsaved work (TMM-B5); nothing else is read.
  const [cardComplete, setCardComplete] = useState(false);
  const cardGuard = useDialogDirtyGuard(
    changeOpen && cardComplete,
    () => {
      setChangeOpen(false);
      setCardComplete(false);
    },
    busy,
  );
  const pendingAbort = useRef<AbortController | null>(null);
  const { statementDescriptor, customerEmails } = useClientConfigDisplay(
    profile?.subscription != null,
  );

  useEffect(() => {
    return () => {
      pendingAbort.current?.abort();
    };
  }, []);

  async function handleCardUpdated(result: PaymentMethodResult) {
    setChangeOpen(false);
    onNotify(
      t('billing.cardUpdated', {
        card: [result.cardBrand, result.maskedCard].filter(Boolean).join(' '),
      }),
    );
    await load();
  }

  async function handleCompletePayment(statement: Statement) {
    setActionNote(null);
    try {
      const action = await billingApi.statementAction(statement.id);
      const result = await completeClientAction(action.clientAction);
      if (result.kind === 'completed') {
        setActionNote(t('billing.actionCompleted'));
        await load();
        return;
      }
      if (result.kind === 'failed') {
        setActionNote(t('billing.actionFailed', { message: result.message ?? '' }));
        return;
      }
      setActionNote(
        t('billing.actionUnsupported', {
          reference: action.clientAction?.type || String(statement.id),
        }),
      );
    } catch {
      setActionNote(t('settings.errorGeneric'));
    }
  }

  async function handleFinishPending() {
    const pending = profile?.pending;
    if (pending?.clientAction == null || pendingBusy) return;
    setActionNote(null);
    setPendingBusy(true);
    pendingAbort.current?.abort();
    const controller = new AbortController();
    pendingAbort.current = controller;
    try {
      const result = await completeClientAction(pending.clientAction);
      if (result.kind !== 'completed') {
        setActionNote(
          result.kind === 'failed'
            ? t('billing.actionFailed', { message: result.message ?? '' })
            : t('card.challengeFailed'),
        );
        return;
      }
      const outcome = await awaitSubscriberActivation(refetchMe, { signal: controller.signal });
      if (outcome === 'aborted') return;
      if (outcome === 'activated') {
        onNotify(t('billing.actionCompleted'));
        await load();
        return;
      }
      setActionNote(t('card.activatingSlow'));
      await load();
    } catch {
      setActionNote(t('settings.errorGeneric'));
    } finally {
      setPendingBusy(false);
    }
  }

  if (profile === null && (status === 'loading' || status === 'idle')) {
    return <Typography variant="body2">{t('common.loading')}</Typography>;
  }
  if (profile === null) {
    return (
      <ErrorState error={loadError} what={t('errors.what.billing')} onRetry={() => void load()} />
    );
  }

  const sub = profile.subscription;
  const pending = profile.pending;
  const cardLabel = [sub?.cardBrand, sub?.maskedCard].filter(Boolean).join(' ');
  const promo = profile.promotion;
  const referral = profile.referral;

  return (
    <Stack spacing={4}>
      {pending != null && (
        <Alert
          severity="info"
          action={
            pending.clientAction != null ? (
              // In flight, the label keeps the alert's `infoText` ink (a disabled button would
              // paint `text3` on `infoSoft`, an unguaranteed pair, TMMAPP-0054): busy is the
              // spinner in that ink plus `aria-busy` / `aria-disabled`, and the handler ignores a
              // second press.
              <Button
                color="inherit"
                size="small"
                aria-disabled={pendingBusy || undefined}
                aria-busy={pendingBusy || undefined}
                onClick={() => void handleFinishPending()}
                startIcon={
                  pendingBusy ? (
                    <CircularProgress size={14} color="inherit" aria-hidden />
                  ) : undefined
                }
              >
                {t('billing.finishNow')}
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => void load()}>
                {t('billing.refresh')}
              </Button>
            )
          }
        >
          {t(pending.clientAction != null ? 'billing.pendingCard' : 'billing.settingUp')}
        </Alert>
      )}

      {sub &&
        (sub.status.toLowerCase() === 'past_due' || sub.status.toLowerCase() === 'paused') && (
          <Alert severity="warning">
            {t(`billing.subscriptionStatus.${sub.status.toLowerCase()}`)}
          </Alert>
        )}
      {/* The AI wallet sits in this panel too: one banner covers both (TMMAPP-0119 item 11). */}
      <TaxLocationBanner
        shown={profile.taxLocationRequired && pending == null}
        onUpdateCard={() => setChangeOpen(true)}
      />
      <Box>
        <Typography variant="h3" gutterBottom>
          {t('billing.cardOnFile')}
        </Typography>
        <Typography variant="mono" component="p" sx={{ mb: 1 }}>
          {cardLabel || t('billing.noCard')}
        </Typography>
        {statementDescriptor !== null && (
          <Typography
            variant="body2"
            component="p"
            data-statement-descriptor=""
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            {t('billing.statementDescriptor', { descriptor: statementDescriptor })}
          </Typography>
        )}
        <Typography
          variant="body2"
          component="p"
          data-renewal-notices=""
          sx={{ color: 'text.secondary', mb: 1 }}
        >
          {t('billing.renewalNotices')}
        </Typography>
        {/* One processor sentence: the served list, or the fixed fallback. Never both. */}
        {customerEmails.length > 0 ? (
          <Typography
            variant="body2"
            component="p"
            data-stripe-emails=""
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            {t('billing.stripeEmails', {
              emails: formatList(
                customerEmails.map((token) => t(`billing.stripeEmail.${token}`)),
                locale,
              ),
            })}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            component="p"
            data-processor-emails=""
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            {t('billing.processorEmails')}
          </Typography>
        )}
        <Button variant="outlined" onClick={() => setChangeOpen(true)} disabled={pending != null}>
          {t('billing.changeCard')}
        </Button>
      </Box>

      {pending == null && sub && profile.prices && (
        <Box key={sub.retailOfferId}>
          <Typography variant="body2" color="text.secondary">
            {t('billing.planRef')}
          </Typography>
          <Typography component="p">
            <MoneyText minor={profile.prices.baseMinor} currency={profile.prices.currency} />
          </Typography>
        </Box>
      )}

      {promo && (
        <Box>
          <Typography variant="h3" gutterBottom>
            {t('billing.promotionTitle')}
          </Typography>
          <Typography variant="body1">{promo.name}</Typography>
          {promo.kind === 'PERCENT_OFF_VARIABLE' && promo.percentBp !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.promotionTermsPercent', {
                percent: bpToPercentText(promo.percentBp, locale),
              })}
            </Typography>
          )}
          {promo.kind === 'AMOUNT_OFF_VARIABLE' &&
            promo.amountMinor !== null &&
            promo.currency !== null && (
              <Typography variant="body2" color="text.secondary">
                <Trans
                  i18nKey="billing.promotionTermsAmount"
                  components={{
                    amount: <MoneyText minor={promo.amountMinor} currency={promo.currency} />,
                  }}
                />
              </Typography>
            )}
          <Typography variant="body2" color="text.secondary">
            {t('billing.promotionApplied', { date: formatWhen(promo.appliedAt, locale, zone) })}
          </Typography>
          {promo.statementsRemaining !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.promotionRemaining', { count: promo.statementsRemaining })}
            </Typography>
          )}
        </Box>
      )}

      {referral && (
        <Box>
          <Typography variant="body1">
            {t('billing.referredBy', { name: referral.affiliateDisplayName })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatWhen(referral.attributedAt, locale, zone)}
          </Typography>
          {referral.changeableUntil && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.changeableUntil', {
                when: formatWhen(referral.changeableUntil, locale, zone),
              })}
            </Typography>
          )}
        </Box>
      )}

      <Box>
        <Typography variant="h3" gutterBottom>
          {t('billing.sponsoredTitle')}
        </Typography>
        {profile.sponsoredSeats.length === 0 ? (
          <Typography variant="body2">{t('billing.emptySeats')}</Typography>
        ) : (
          <Table size="small" aria-label={t('billing.sponsoredTitle')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('billing.colGuest')}</TableCell>
                <TableCell>{t('billing.colSince')}</TableCell>
                <TableCell>{t('billing.colStatus')}</TableCell>
                <TableCell align="right">{t('billing.colMonthly')}</TableCell>
                <TableCell>{t('billing.colEnds')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.sponsoredSeats.map((seat) => (
                <TableRow key={seat.userId}>
                  <TableCell>{seat.guestDisplayName}</TableCell>
                  <TableCell>{formatWhen(seat.since, locale, zone)}</TableCell>
                  <TableCell>
                    <StatusChip domain="subscription" status={seat.status} />
                  </TableCell>
                  <TableCell align="right">
                    <MoneyText minor={seat.monthlyMinor} currency={seat.currency} />
                  </TableCell>
                  <TableCell>{seat.endsAt ? formatWhen(seat.endsAt, locale, zone) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {me.tier === 'SUBSCRIBER' && profile.aiWallet !== null && (
        <AiWalletSection wallet={profile.aiWallet} zone={zone} onNotify={onNotify} />
      )}
      {/* The product admin's manual AI suspension only (TMMAPI-0152): one sentence, never billing. */}
      {profile.aiSuspendedAt && <Alert severity="warning">{t('ai.reason.SUSPENDED')}</Alert>}

      <Box>
        <Typography variant="h3" gutterBottom>
          {t('billing.statementsTitle')}
        </Typography>
        <SellerLine sx={{ mb: 1 }} />
        {actionNote && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {actionNote}
          </Alert>
        )}
        {profile.statements.length === 0 ? (
          <Typography variant="body2">{t('billing.emptyStatements')}</Typography>
        ) : (
          <Table size="small" aria-label={t('billing.statementsTitle')}>
            <TableHead>
              <TableRow>
                <ActionsHeaderCell padding="checkbox" />
                <TableCell>{t('billing.colPeriod')}</TableCell>
                <TableCell align="right">{t('billing.colTotal')}</TableCell>
                <TableCell>{t('billing.colStatus')}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('billing.colRenewal')}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('billing.colSeats')}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('billing.colTax')}
                </TableCell>
                <ActionsHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {profile.statements.map((row) => (
                <StatementRows
                  key={row.id}
                  statement={row}
                  expanded={expanded === row.id}
                  onToggle={() => setExpanded((id) => (id === row.id ? null : row.id))}
                  onComplete={() => void handleCompletePayment(row)}
                  locale={locale}
                  zone={zone}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <GuardedDialog guard={cardGuard} open={changeOpen} fullWidth maxWidth="sm">
        <DialogTitle>{t('billing.changeCard')}</DialogTitle>
        <SellerLine dialog />
        <DialogContent>
          <ChangeCardForm
            onUpdated={handleCardUpdated}
            onCompleteChange={setCardComplete}
            onBusyChange={setBusy}
            onFailed={cardGuard.focusErrors}
          />
        </DialogContent>
        <DialogActions>
          <DialogDoneButton onClick={cardGuard.requestClose} disabled={busy} />
        </DialogActions>
      </GuardedDialog>
    </Stack>
  );
}

/**
 * "{jurisdiction} {rate}" from pay-api's served text. A bare-number rate is the locale's percent
 * (`formatPercentText`); any other served text shows verbatim. It is never computed with.
 */
function taxLineLabel(
  t: TFunction,
  line: StatementTaxLine,
  locale: string,
  nested: boolean,
): string {
  const rate = formatPercentText(line.ratePercent ?? '', locale);
  // Under a Tax heading the word is already shown. A blank jurisdiction is just the rate.
  const jurisdiction = line.jurisdiction?.trim() || (nested ? '' : t('billing.lineKind.TAX'));
  if (jurisdiction.length === 0) return rate;
  return rate.length > 0 ? t('billing.taxJurisdiction', { jurisdiction, rate }) : jurisdiction;
}

/** RENEWAL and SEAT keep "Kind — description". Every other kind is its catalog label alone. */
function statementLineLabel(t: TFunction, line: StatementLine): string {
  if (line.kind === 'OTHER') return t('billing.lineOther');
  if (line.kind === 'RENEWAL' || line.kind === 'SEAT') {
    return t('billing.statementLine', {
      kind: t(`billing.lineKind.${line.kind}`),
      description: line.description,
    });
  }
  return t(`billing.lineKind.${line.kind}`);
}

function StatementRows({
  statement,
  expanded,
  onToggle,
  onComplete,
  locale,
  zone,
}: {
  statement: Statement;
  expanded: boolean;
  onToggle: () => void;
  onComplete: () => void;
  locale: string;
  zone: string;
}) {
  const { t } = useTranslation();
  const currency = statement.currency;
  const nestTax =
    statement.taxLines.length > 0 && statement.lines.some((line) => line.kind === 'TAX');
  const visibleLines = nestTax
    ? statement.lines.filter((line) => line.kind !== 'TAX')
    : statement.lines;
  const period = [statement.periodStart, statement.periodEnd]
    .map((part) => formatWhen(part, locale, zone))
    .join(' – ');

  return (
    <Fragment>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={t('billing.rowDetails')}
            aria-expanded={expanded}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{period || '—'}</TableCell>
        <TableCell align="right">
          <MoneyText minor={statement.totalMinor} currency={currency} />
        </TableCell>
        <TableCell>
          <StatusChip domain="charge" status={statement.chargeStatus} />
        </TableCell>
        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <MoneyText minor={statement.renewalMinor} currency={currency} />
        </TableCell>
        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <MoneyText minor={statement.seatsMinor} currency={currency} />
        </TableCell>
        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <MoneyText minor={statement.taxMinor} currency={currency} />
        </TableCell>
        <TableCell>
          {statement.chargeStatus === 'ACTION_REQUIRED' && (
            <Button size="small" onClick={onComplete}>
              {t('billing.completePayment')}
            </Button>
          )}
          {statement.chargeStatus === 'FAILED' && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.chargeFailed', {
                failed: t('errors.paymentFailed'),
                hint: t('billing.updateCardHint'),
              })}
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1 }}>
              {visibleLines.map((line, index) => (
                <Typography
                  key={`${line.kind}-${index}`}
                  variant="body2"
                  data-statement-line={line.kind}
                >
                  {statementLineLabel(t, line)}{' '}
                  <MoneyText
                    minor={line.amountMinor}
                    currency={currency}
                    tone={line.kind === 'PROMOTION' ? 'discount' : 'balance'}
                  />
                </Typography>
              ))}
              {nestTax && (
                <Typography variant="body2" data-statement-line="TAX">
                  {t('billing.lineKind.TAX')}
                </Typography>
              )}
              {/* Stripe Tax's jurisdictions, one row each (TMMAPP-0119 item 10). With a TAX line they sit under that heading. */}
              {statement.taxLines.map((line, index) => (
                <Typography
                  key={`tax-${index}`}
                  variant="body2"
                  data-statement-tax-line=""
                  sx={{ paddingInlineStart: 2 }}
                >
                  {taxLineLabel(t, line, locale, nestTax)}{' '}
                  <MoneyText minor={line.amountMinor} currency={currency} />
                </Typography>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
